<template>
  <div
    class="container"
    tabindex="0"
    @keydown="onKeydown"
    @mousemove="onMouseMove"
    ref="container"
  >
    <div class="player" v-if="isPlayback">
      <div id="viewport"></div>
      <div id="toolbar" v-if="isShowToolbar">
        <div
          class="play-pause"
          :class="{ playing: isPlaying }"
          @click="togglePlay"
        >
          <span class="play-icon"></span>
        </div>
        <div class="played">{{ currentTime }} / {{ duration }}</div>

        <el-select
          name="speed"
          class="speed"
          v-model="speed"
          placeholder="倍速"
          size="mini"
          @change="onPlaybackRateChange"
        >
          <el-option
            v-for="item in speeds"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-select>

        <div class="scrubber" @click="seek" ref="scrubber">
          <el-progress :percentage="percent" :show-text="false" />
        </div>

        <div
          class="fullscreen"
          :class="{ fullscreening: isFullscreen }"
          @click="toggleFullscreen"
        >
          <span class="fullscreen-icon"></span>
        </div>
      </div>
    </div>

    <iframe
      v-if="!isPlayback && streamingUrl"
      :src="streamingUrl"
      frameborder="0"
      class="streaming-iframe"
    />
  </div>
</template>

<script>
import screenfull from "screenfull";

function formatTime(time) {
  const m = Math.floor(time / 60);
  const s = Math.floor(time % 60);
  const minute = m > 9 ? m : "0" + m;
  const second = s > 9 ? s : "0" + s;
  return minute + ":" + second;
}

export default {
  name: "PlasoCloudClass",
  data() {
    return {
      isLoaded: false,
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      player: null,
      isPlayback: true,
      streamingUrl: "",
      percent: 0,
      speed: 1,
      isFullscreen: false,
      keymap: {
        Space: "togglePlay",
        ArrowLeft: "backward",
        ArrowRight: "fastward",
      },
      speeds: [
        {
          value: 0.5,
          label: "0.5",
        },
        {
          value: 0.75,
          label: "0.75",
        },
        {
          value: 1,
          label: "正常",
        },
        {
          value: 1.25,
          label: "1.25",
        },

        {
          value: 1.5,
          label: "1.5",
        },
        {
          value: 2,
          label: "2",
        },
      ],
      isShowToolbar: true,
      timeoutId: null,
      lastUpdateProgressAt: 0,
    };
  },
  methods: {
    onMouseMove() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.isShowToolbar = true;
      this.timeoutId = setTimeout(() => {
        this.isShowToolbar = false;
      }, 3000);
    },
    toggleFullscreen() {
      if (screenfull.isEnabled) {
        screenfull.toggle(this.$refs.container);
      }
    },
    onPlaybackRateChange(rate) {
      this.player.playbackRate = rate;
    },
    onPlayerReady(player) {
      this.isLoaded = true;
      this.player = player;
    },
    fastward() {
      if (!this.isLoaded || !this.player) {
        return;
      }

      this.player.currentTime += 5;
    },
    backward() {
      if (!this.isLoaded || !this.player) {
        return;
      }

      this.player.currentTime -= 5;
    },
    togglePlay() {
      if (!this.isLoaded || !this.player) {
        return;
      }
      const player = this.player;
      const isPlaying = this.isPlaying;

      if (isPlaying) {
        player.pause().then(() => {
          this.isPlaying = false;
        });
      } else {
        if (player.ended) {
          this.player.currentTime = 0;
        }
        player.play().then(() => {
          this.isPlaying = true;
        });
      }
    },
    seek(event) {
      if (!this.isLoaded || !this.player) {
        return;
      }
      const scrubber = this.$refs.scrubber;
      const left = scrubber.getBoundingClientRect().left;
      const percent = (event.clientX - left) / scrubber.offsetWidth;
      this.percent = percent * 100;
      this.player.currentTime = percent * this.player.duration;
    },
    onTimeUpdate() {
      if (!this.isLoaded || !this.player) {
        return;
      }
      const currentTime = this.player.currentTime;
      this.currentTime = formatTime(currentTime);
      this.duration = formatTime(this.player.duration);
      this.percent = (this.player.currentTime / this.player.duration) * 100;
      const now = Date.now();
      const diff = now - this.lastUpdateProgressAt;
      const interval = 5 * 1000;
      if (diff > interval && currentTime >= 1) {
        this.updateProgress(currentTime);
        this.lastUpdateProgressAt = now;
      }
    },
    updateProgress(currentTime) {
      const meetingId = this.$route.query.meetingId;
      this.$http.post("/api/app/course/class_progress", {
        meetingId,
        currentTime,
      });
    },
    onKeydown(event) {
      const code = event.code;
      const functionName = this.keymap[code];
      if (this[functionName]) {
        this[functionName]();
      }
    },
    onEnded() {
      this.isPlaying = false;
    },
    initPlayer(result) {
      const classUrl = result.class_url;
      const currentTime = result.current_time;
      if (result.is_playback) {
        this.isPlayback = true;

        const container = document.getElementById("viewport");
        const sdkPlayer = new window.PlasoUpimePlayer(container);
        const query = result.query;
        const option = {};

        sdkPlayer.setSrc(query, option);
        sdkPlayer.on("canplay", () => {
          if (currentTime) {
            sdkPlayer.currentTime = currentTime;
          }

          this.onPlayerReady(sdkPlayer);
          this.onTimeUpdate();
        });
        sdkPlayer.on("timeupdate", this.onTimeUpdate);
        sdkPlayer.on("click", this.togglePlay);
        sdkPlayer.on("ended", this.onEnded);
      } else {
        this.isPlayback = false;
        this.streamingUrl = classUrl;
      }
    },
  },
  mounted() {
    const meetingId = this.$route.query.meetingId;
    this.$http
      .get("/api/app/course/class_info", {
        meetingId,
        appType: "liveclassSDK",
        userType: "listener",
        meetingType: "private",
        mediaType: "audio",
      })
      .then(this.initPlayer);

    screenfull.on("change", () => {
      this.player.resize();
      this.isFullscreen = screenfull.isFullscreen;
    });
  },
  beforeDestroy() {
    this.player.pause();
    this.isLoaded = false;
    this.player = null;
  },
};
</script>
<style>
#start {
  margin-left: 20px;
}

.container {
  height: 100%;
  width: 100%;
  min-height: inherit;
  outline: none;
  background-color: white;
}

.player {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  position: relative;
  min-height: inherit;
}

#viewport {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  min-height: inherit;
}

#toolbar {
  position: absolute;
  z-index: 10;
  width: 100%;
  height: 60px;
  left: 0;
  bottom: 0;
  /* padding-right: 18px; */
  font-size: 12px;
  display: flex;
  align-items: center;
}

#toolbar::before {
  content: "";
  position: absolute;
  height: 120px;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: -1;
  pointer-events: none;
  background-image: linear-gradient(
    rgba(128, 128, 128, 0) 0%,
    rgba(115, 115, 115, 0.04) 22%,
    rgba(19, 19, 19, 0.15) 75%,
    rgba(0, 0, 0, 0.25) 100%
  );
}

.play-pause {
  width: 60px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-icon {
  cursor: pointer;
  width: 20px;
  height: 20px;
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(data:image/png;base64,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);
}

.playing .play-icon {
  background-image: url(data:image/png;base64,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);
}

.played {
  text-align: left;
  height: 20px;
  line-height: 20px;
  font-size: 14px;
  color: #ffffff;
  width: 120px;
}

.scrubber {
  flex-grow: 1;
  position: relative;
  height: 44px;
  margin: 0px;
  border-left: 0px;
  border-bottom: 0px;
  overflow: visible;
  padding: 40px 10px 0 10px;
  cursor: pointer;
}

#leave {
  position: absolute;
  right: 20px;
  background: #999;
  z-index: 10;
}

.speed {
  width: 70px;
}

.fullscreen {
  width: 60px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-icon {
  cursor: pointer;
  width: 20px;
  height: 20px;
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgOTYgOTYwIDk2MCIgd2lkdGg9IjQ4Ij48cGF0aCBkPSJNMjAwIDg1NlY2NThoMjJ2MTc2aDE3NnYyMkgyMDBabTAtMzYyVjI5NmgxOTh2MjJIMjIydjE3NmgtMjJabTM2MiAzNjJ2LTIyaDE3NlY2NThoMjJ2MTk4SDU2MlptMTc2LTM2MlYzMThINTYydi0yMmgxOTh2MTk4aC0yMloiLz48L3N2Zz4=);
}

.fullscreening .fullscreen-icon {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgOTYgOTYwIDk2MCIgd2lkdGg9IjQ4Ij48cGF0aCBkPSJNMzc2IDg1NlY2ODBIMjAwdi0yMmgxOTh2MTk4aC0yMlptMTg2IDBWNjU4aDE5OHYyMkg1ODR2MTc2aC0yMlpNMjAwIDQ5NHYtMjJoMTc2VjI5NmgyMnYxOThIMjAwWm0zNjIgMFYyOTZoMjJ2MTc2aDE3NnYyMkg1NjJaIi8+PC9zdmc+);
}

#service {
  display: none;
}

.streaming-iframe {
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
}
</style>
